/**
 * Branding configuration for white-label support
 */
export interface BrandingConfig {
  companyName: string
  logoUrl: string | null
  primaryColor: string
  accentColor: string
  faviconUrl: string | null
}

/**
 * Default branding (LogiDocs Certify)
 */
export const DEFAULT_BRANDING: BrandingConfig = {
  companyName: 'LogiDocs Certify',
  logoUrl: '/logidocs/logo-color.png',
  primaryColor: '#2d3a8b',
  accentColor: '#f97316',
  faviconUrl: null,
}

/**
 * Convert hex color to HSL values for CSS variables
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse hex values
  if (hex.length !== 6) return null

  const rRaw = parseInt(hex.slice(0, 2), 16)
  const gRaw = parseInt(hex.slice(2, 4), 16)
  const bRaw = parseInt(hex.slice(4, 6), 16)
  if (isNaN(rRaw) || isNaN(gRaw) || isNaN(bRaw)) return null

  const r = rRaw / 255
  const g = gRaw / 255
  const b = bRaw / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

/**
 * Format HSL for CSS variable (without hsl() wrapper)
 */
export function formatHslForCss(hsl: { h: number; s: number; l: number }): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`
}
